// Очередь загрузки фото витрины (06.10.2026).
//
// Было (решение 01.10): все фото карточек отдавались браузеру разом — через 2 с первые фото
// ВСЕХ карточек переходили в eager, а srcSet на широком окне с Retina (Telegram Desktop)
// выбирал large. У Стемса — 206 файлов / ~19 МБ за открытие; запросы данных каталога стояли
// в той же очереди HTTP/2 за картинками: ушёл в корзину, вернулся — «Загружаем…» до 30 с.
//
// Теперь (решение 06.10):
//   • в каталоге только средний размер (600 px), не больше MAX_PARALLEL файлов одновременно —
//     запросам данных всегда остаётся канал;
//   • сначала первые фото карточек, потом вторые; внутри — сначала у экрана, дальше сверху вниз;
//   • ушёл из вкладки — её ожидающие фото снимаются с очереди (докачиваются только те, что в пути);
//   • открыта карточка (зум) — каталог на паузе, большие фото этой карточки по одному: с текущего
//     и дальше по порядку; пока большое не скачано, в зуме стоит среднее того же фото;
//   • очередь опустела — первые фото первого экрана соседней вкладки (setIdlePrefetch).
// Скачанный файл лежит в кэше браузера (Cache-Control на /uploads — месяц): <img> с тем же
// адресом показывает его сразу.
import { useEffect, useReducer, useRef } from 'react';

const MAX_PARALLEL = 2;
// Загрузка «повисла» (экран заблокирован, смена Wi-Fi → LTE): обрываем, один повтор,
// иначе все слоты очереди стоят до таймаута браузера, а каталог — с заглушками.
// Повтору срок вдвое больше: на живой, но очень медленной сети (~5 КБ/с) файл мог просто
// не успеть — второй раз не обрываем его на 95 %.
const STALL_MS = 20000;
// Повтор — не сразу: Chromium снимает оборванный запрос отдельной задачей, и мгновенный
// повтор того же адреса может прицепиться к той же мёртвой загрузке
const STALL_RETRY_DELAY_MS = 1000;
// Не скачалось (404, обрыв) — новая попытка не раньше чем через столько: иначе каждый
// свайп в зуме переставляет заявки и повторяет тот же 404
const FAILED_RETRY_MS = 30000;

const TIER_ZOOM = 0;     // большие фото открытой карточки — по одному
const TIER_WANTED = 1;   // фото, на которое человек пролистнул карточку, а оно ещё не скачано
const TIER_CATALOG = 2;  // каталог: [фаза (номер фото в карточке), у экрана ? 0 : 1, позиция]
const TIER_IDLE = 3;     // соседняя вкладка, когда всё остальное готово

// Скачанные держим ссылкой на Image: файл гарантированно в памяти документа, даже если
// HTTP-кэш у WebView выключен или переполнен
const loaded = new Map();     // url → Image
const failed = new Map();     // url → когда не скачалось (Date.now())
const stalls = new Map();     // url → сколько раз обрывали по STALL_MS
const retryAt = new Map();    // url → не повторять раньше (после обрыва)
const wants = new Map();      // url → Set(handle{rank}) — кто и с каким приоритетом ждёт файл
const running = new Map();    // url → tier
const subscribers = new Map(); // url → Set(fn) — перерисовать, когда файл скачан
let zoomOpen = 0;
let pumpScheduled = false;
let idleHandles = [];

function cmp(a, b) {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d) return d;
  }
  return 0;
}

function bestRank(url) {
  let best = null;
  const set = wants.get(url);
  if (set) set.forEach(h => { if (!best || cmp(h.rank, best) < 0) best = h.rank; });
  return best;
}

function zoomRunning() {
  let n = 0;
  running.forEach(tier => { if (tier === TIER_ZOOM) n += 1; });
  return n;
}

function eligible(rank) {
  if (rank[0] === TIER_ZOOM) {
    // По одному; на место в очереди не смотрим — каталог в это время на паузе,
    // а в пути от него остаётся не больше MAX_PARALLEL файлов
    return zoomRunning() === 0 && running.size < MAX_PARALLEL + 1;
  }
  if (zoomOpen > 0) return false;
  return running.size < MAX_PARALLEL;
}

function notify(url) {
  const set = subscribers.get(url);
  if (set) Array.from(set).forEach(fn => fn());
}

function start(url, tier) {
  running.set(url, tier);
  const img = new Image();
  img.decoding = 'async';
  let timer = null;
  const settle = () => {
    clearTimeout(timer);
    img.onload = null;
    img.onerror = null;
    running.delete(url);
  };
  img.onload = () => {
    settle();
    stalls.delete(url);
    retryAt.delete(url);
    loaded.set(url, img);
    notify(url);
    pump();
  };
  img.onerror = () => {
    settle();
    stalls.delete(url);
    retryAt.delete(url);
    failed.set(url, Date.now());
    notify(url);
    pump();
  };
  const attempt = stalls.get(url) || 0;
  timer = setTimeout(() => {
    settle();
    img.src = '';   // оборвать зависший запрос
    if (attempt >= 1) {
      stalls.delete(url);
      failed.set(url, Date.now());
      notify(url);
    } else {
      stalls.set(url, 1);   // ещё попытка — в общем порядке очереди, через паузу
      retryAt.set(url, Date.now() + STALL_RETRY_DELAY_MS);
      setTimeout(pump, STALL_RETRY_DELAY_MS + 1);
    }
    pump();   // освободившийся слот — следующему в очереди
  }, STALL_MS * (attempt + 1));
  img.src = url;
}

function pump() {
  pumpScheduled = false;
  for (;;) {
    let pick = null;
    let pickRank = null;
    wants.forEach((_, url) => {
      if (running.has(url) || loaded.has(url) || failed.has(url)) return;
      if ((retryAt.get(url) || 0) > Date.now()) return;
      const rank = bestRank(url);
      if (!rank || !eligible(rank)) return;
      if (!pick || cmp(rank, pickRank) < 0) { pick = url; pickRank = rank; }
    });
    if (!pick) return;
    start(pick, pickRank[0]);
  }
}

// Пересчёт — после того как React закончит монтирование/размонтирование: смена вкладки
// и закрытие зума снимают и ставят много заявок разом, промежуточное состояние не качаем.
function schedulePump() {
  if (pumpScheduled) return;
  pumpScheduled = true;
  setTimeout(pump, 0);
}

function want(url, rank) {
  const noop = { update() {}, release() {} };
  if (!url) return noop;
  const failedAt = failed.get(url);
  if (failedAt !== undefined && Date.now() - failedAt > FAILED_RETRY_MS) failed.delete(url);
  let set = wants.get(url);
  if (!set) { set = new Set(); wants.set(url, set); }
  const handle = { rank };
  set.add(handle);
  schedulePump();
  return {
    update(nextRank) { handle.rank = nextRank; schedulePump(); },
    release() {
      const s = wants.get(url);
      if (s) {
        s.delete(handle);
        if (!s.size) wants.delete(url);
      }
      schedulePump();
    },
  };
}

function subscribe(url, fn) {
  if (!url) return () => {};
  let set = subscribers.get(url);
  if (!set) { set = new Set(); subscribers.set(url, set); }
  set.add(fn);
  return () => {
    set.delete(fn);
    if (!set.size) subscribers.delete(url);
  };
}

// Одна IntersectionObserver на все карточки; «у экрана» — с запасом 200px, чтобы при
// прокрутке следующий ряд уже качался.
let observer = null;
const observed = new Map(); // element → fn(visible)
function observe(el, fn) {
  if (!el || typeof IntersectionObserver === 'undefined') return () => {};
  if (!observer) {
    observer = new IntersectionObserver(entries => {
      entries.forEach(e => { const cb = observed.get(e.target); if (cb) cb(e.isIntersecting); });
    }, { rootMargin: '200px 0px' });
  }
  observed.set(el, fn);
  observer.observe(el);
  return () => {
    observed.delete(el);
    observer.unobserve(el);
  };
}

// Сверху вниз, в ряду — слева направо
function positionOf(el) {
  if (!el) return 0;
  const r = el.getBoundingClientRect();
  return Math.round(r.top + (window.scrollY || 0)) * 10000 + Math.max(0, Math.round(r.left));
}

const catalogRank = (phase, visible, position) => [TIER_CATALOG, phase, visible ? 0 : 1, position];

export function isImageLoaded(url) {
  return loaded.has(url);
}

// Фото карточки каталога (средний размер). elRef — контейнер фото карточки (позиция и
// видимость), currentIndex — фото, которое сейчас показывает карусель карточки.
// Возвращает массив «скачано ли i-е фото»: до этого карточка показывает заглушку.
export function useCardImages(urls, elRef, currentIndex = 0) {
  const [, rerender] = useReducer(x => x + 1, 0);
  const key = urls.join('|');
  const ready = urls.map(url => loaded.has(url));
  const renderedRef = useRef(ready);
  renderedRef.current = ready;
  const startIndexRef = useRef(currentIndex);
  const swipedRef = useRef(false);

  useEffect(() => {
    if (!urls.length) return undefined;
    const el = elRef.current;
    let visible = false;
    const handles = urls.map((url, i) => want(url, catalogRank(i, visible, positionOf(el))));
    const unsubscribe = urls.map(url => subscribe(url, rerender));
    // Файл мог докачаться между рендером и подпиской (был в пути от прошлого показа или
    // от прогрева соседней вкладки) — тогда уведомление ушло в пустоту
    if (urls.some((url, i) => loaded.has(url) !== renderedRef.current[i])) rerender();
    const unobserve = observe(el, (isVisible) => {
      visible = isVisible;
      const position = positionOf(el);
      handles.forEach((h, i) => h.update(catalogRank(i, visible, position)));
    });
    return () => {
      handles.forEach(h => h.release());
      unsubscribe.forEach(fn => fn());
      unobserve();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Пролистнул карточку на фото, которое ещё не скачано, — оно первое в очереди. Только
  // после настоящего пролистывания: при монтировании этот приоритет у первого фото КАЖДОЙ
  // карточки перебил бы порядок каталога (у экрана, сверху вниз)
  useEffect(() => {
    if (!swipedRef.current && currentIndex === startIndexRef.current) return undefined;
    swipedRef.current = true;
    const url = urls[currentIndex];
    if (!url || loaded.has(url)) return undefined;
    const handle = want(url, [TIER_WANTED, 0]);
    return () => handle.release();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, currentIndex]);

  return ready;
}

// Большие фото открытой карточки: каталог на паузе, большие качаются по одному — с текущего
// и дальше по порядку (перелистнул на ещё не скачанное — оно первое). Возвращает массив
// «скачано ли большое i-е фото».
export function useZoomImages(urls, index) {
  const [, rerender] = useReducer(x => x + 1, 0);
  const key = urls.join('|');
  const ready = urls.map(url => loaded.has(url));
  const renderedRef = useRef(ready);
  renderedRef.current = ready;

  useEffect(() => {
    zoomOpen += 1;
    return () => { zoomOpen -= 1; schedulePump(); };
  }, []);

  useEffect(() => {
    const n = urls.length;
    if (!n) return undefined;
    const handles = urls.map((url, i) => want(url, [TIER_ZOOM, (i - index + n) % n]));
    const unsubscribe = urls.map(url => subscribe(url, rerender));
    if (urls.some((url, i) => loaded.has(url) !== renderedRef.current[i])) rerender();
    return () => {
      handles.forEach(h => h.release());
      unsubscribe.forEach(fn => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, index]);

  return ready;
}

// Первые фото первого экрана соседней вкладки — когда в очереди больше ничего нет.
// Новый вызов заменяет прежний список, [] — снять.
export function setIdlePrefetch(urls) {
  idleHandles.forEach(h => h.release());
  idleHandles = (urls || []).filter(Boolean).map((url, i) => want(url, [TIER_IDLE, i]));
}

// Для тестов: сброс состояния и синхронный пересчёт
export const __test = {
  reset() {
    loaded.clear(); failed.clear(); stalls.clear(); retryAt.clear(); wants.clear(); running.clear();
    subscribers.clear(); observed.clear(); observer = null;
    zoomOpen = 0; pumpScheduled = false; idleHandles = [];
  },
  STALL_MS, STALL_RETRY_DELAY_MS, FAILED_RETRY_MS,
  want,
  pump,
  running: () => Array.from(running.keys()),
  setZoomOpen(n) { zoomOpen = n; },
  TIER_ZOOM, TIER_WANTED, TIER_IDLE,
  catalogRank,
};
